import { cn } from '@virginexperiencedays/tailwind';

import { ContentCard } from '@virginexperiencedays/components-v2/src/cards/ContentCard';
import { Section } from '@virginexperiencedays/components-v2/src/layout/Section';
import { Button } from '@virginexperiencedays/components-v2/src/layout/Button';
import { SectionHeading } from '@virginexperiencedays/components-v2/src/typography/SectionHeading';

import { ReviewCard, ReviewHeading } from '../Reviews/Reviews';

import type { HTMLAttributes } from 'react';
import type { Reviews } from '../VariationPage/extractors';
import type { ReviewsOverlayProps } from '../Reviews/ReviewsOverlay';

type FeaturedReviewsWidgetProps = HTMLAttributes<HTMLDivElement> &
  Pick<ReviewsOverlayProps, 'onExpandCollapse'> & {
    featured: Reviews['featured'];
  };

export const FeaturedReviewsWidget = ({
  featured: reviews,
  onExpandCollapse,
  ...rest
}: FeaturedReviewsWidgetProps) => {
  if (!reviews?.length) return null;

  return (
    <section {...rest}>
      <div
        className={cn(
          'grid grid-cols-1 gap-4',
          reviews.length === 1 ? 'md:grid-cols-1' : 'md:grid-cols-2'
        )}
      >
        {reviews.map((review, index) => (
          <ContentCard
            key={`${review.title}-${review.reviewerName}`}
            className={cn(
              'p-4',
              // mobile show 1, desktop show 2
              index > 0 ? 'hidden md:flex' : 'flex'
            )}
          >
            <ReviewCard
              {...review}
              response={null}
              isContentFlexGrow
              onExpandCollapse={({ isExpanded }) => {
                onExpandCollapse?.({
                  currentIndex: index,
                  total: reviews?.length,
                  isExpanded,
                  rating: review?.rating,
                  dateReviewed: review?.dateCreated,
                  moduleTitle: review?.title,
                });
              }}
            />
          </ContentCard>
        ))}
      </div>
    </section>
  );
};

type CustomerReviewsWidgetProps = HTMLAttributes<HTMLDivElement> &
  Pick<Reviews, 'averageRating' | 'averageRatingDisplay' | 'count' | 'reviews'> &
  Pick<ReviewsOverlayProps, 'onExpandCollapse'> & {
    onClickAllReviews?: () => void;
  };

export const CustomerReviewsWidget = ({
  className,
  averageRating,
  averageRatingDisplay,
  count,
  reviews,
  onClickAllReviews,
  onExpandCollapse,
  ...rest
}: CustomerReviewsWidgetProps) => {
  if (!reviews?.length || !count) return null;

  return (
    <Section className={cn('flex-start flex flex-col gap-4', className)} {...rest}>
      <SectionHeading>Customer Reviews</SectionHeading>

      <ReviewHeading
        averageRating={averageRatingDisplay}
        count={count}
        className="mb-4"
        data-rating={averageRating}
      />

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-x-16 md:gap-y-10">
        {reviews.slice(0, 6).map(({ reply, responderName, ...review }, index) => (
          <ReviewCard
            key={review.title}
            className={cn(
              // mobile show 3, desktop show 6
              index > 2 ? 'hidden md:flex' : 'flex'
            )}
            {...review}
            response={{ content: reply, responderName }}
            onExpandCollapse={({ isExpanded }) => {
              onExpandCollapse?.({
                currentIndex: index,
                total: reviews?.length,
                isExpanded,
                rating: review?.rating,
                dateReviewed: review?.dateCreated,
                moduleTitle: review?.title,
              });
            }}
          />
        ))}
      </div>

      {/* All Reviews Modal/Drawer Trigger */}
      {reviews.length > 3 && (
        <Button
          className="mt-4 h-12 p-4 md:mt-10 md:max-w-[304px]"
          size="large"
          variant="outline"
          onClick={onClickAllReviews}
        >
          Read All Reviews
        </Button>
      )}
    </Section>
  );
};
